import {
  FIELD_KEYBOARD_GAP,
  getScrollContent,
  getScrollParent,
  scrollFieldAboveKeyboard
} from '../MathKeyboard/keyboardLift';

const LIFT_ATTRIBUTE = 'data-math-keyboard-lift';

// floating-ui repositions the overlay only after the keyboard-driven layout lands; re-clamp the
// transform once that has settled.
const CONTAINER_SETTLE_MS = 300;

// Opt-in marker for floating overlays (the math popover dropdowns) that must be shifted, as a whole,
// above MathLive's virtual keyboard: they are viewport-positioned, so no scroll can reveal them.
// Spread onto the overlay element, e.g. `<Popover.Dropdown {...MATH_KEYBOARD_LIFT_PROPS}>`.
export const MATH_KEYBOARD_LIFT_PROPS = { [LIFT_ATTRIBUTE]: '' } as const;

// Only one author field is focused at a time. A new lift stops the previous one SYNCHRONOUSLY so
// its restore runs before the new lift captures baselines — the previous field's blur-deferred stop
// (100ms) would otherwise restore stale padding/margins over the new lift's, leaving the new field
// covered and leaking padding on its own restore.
let activeLiftStop: (() => void) | null = null;

/**
 * Keep an author `<math-field>` visible while MathLive's virtual keyboard is open (bug 535585).
 *
 * The keyboard is a viewport-fixed bottom bar that paints above every overlay (its z-index is raised
 * in MathLiveField.css), so nothing escapes it by stacking — the field has to move. Two moves, by
 * container:
 * - Field inside an overlay marked with MATH_KEYBOARD_LIFT_PROPS: translate the whole overlay (field
 *   AND its action buttons) up until it clears the keyboard. Mantine positions dropdowns via
 *   top/left, so the transform doesn't fight floating-ui's repositioning. The overlay's anchor (the
 *   formula being edited) is also scrolled into view with room reserved below it for the overlay, so
 *   both stay visible and the overlay can rest at its natural offset under the anchor instead of
 *   being translated over it.
 * - Otherwise: scroll the field's scrollable ancestor clear of the keyboard, adding scroll room when
 *   there is none — the same lift the student keyboard's provider performs against its dock.
 *
 * Driven by the keyboard's own geometry events, so it also undoes itself when the keyboard hides
 * mid-focus (the in-field toggle). Returns a stop() that restores every touched style.
 */
export function startVirtualKeyboardLift(field: HTMLElement): () => void {
  const keyboard = window.mathVirtualKeyboard;
  if (!keyboard) return () => undefined;
  activeLiftStop?.();

  const overlay = field.closest<HTMLElement>(`[${LIFT_ATTRIBUTE}]`);
  // The overlay's anchor: Mantine wires the popover target's aria-controls to the dropdown's id.
  const anchor = overlay?.id ? document.querySelector<HTMLElement>(`[aria-controls="${overlay.id}"]`) : null;
  const scrollParent = overlay ? null : getScrollParent(field);
  const scrollContent = overlay ? null : getScrollContent(field, scrollParent);
  const previousTransform = overlay?.style.transform ?? '';
  const previousAnchorMarginTop = anchor?.style.scrollMarginTop ?? '';
  const previousAnchorMarginBottom = anchor?.style.scrollMarginBottom ?? '';
  const previousScrollMargin = field.style.scrollMarginBottom;
  const previousPadding = scrollContent?.style.paddingBottom ?? '';
  let appliedLift = 0;

  const adjust = (scrollIntoView: boolean) => {
    // A hidden keyboard reports a zero rect — treat its top edge as the viewport bottom.
    const rect = keyboard.boundingRect;
    const keyboardTop = keyboard.visible && rect.height > 0 ? rect.top : window.innerHeight;

    if (overlay) {
      // The overlay's rect includes the current transform; add it back to measure the resting box.
      const overlayRect = overlay.getBoundingClientRect();
      const baseBottom = overlayRect.bottom + appliedLift;
      const baseTop = overlayRect.top + appliedLift;
      const wanted = Math.ceil(baseBottom - (keyboardTop - FIELD_KEYBOARD_GAP));
      // Never push the overlay's top edge off-screen.
      const nextLift = Math.max(0, Math.min(wanted, Math.floor(baseTop - FIELD_KEYBOARD_GAP)));
      if (nextLift !== appliedLift) {
        appliedLift = nextLift;
        overlay.style.transform = appliedLift > 0 ? `translateY(${-appliedLift}px)` : previousTransform;
      }
      // Bring the anchor into view, reserving the overlay's height below it so the overlay can sit
      // at its natural spot under the anchor (with the popover's own gap) rather than on top of it.
      if (scrollIntoView && anchor && keyboardTop < window.innerHeight) {
        anchor.style.scrollMarginTop = `${FIELD_KEYBOARD_GAP}px`;
        anchor.style.scrollMarginBottom = `${Math.ceil(overlayRect.height) + FIELD_KEYBOARD_GAP}px`;
        anchor.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return;
    }

    if (keyboardTop >= window.innerHeight) {
      field.style.scrollMarginBottom = previousScrollMargin;
      if (scrollContent) scrollContent.style.paddingBottom = previousPadding;
      return;
    }
    if (scrollIntoView) scrollFieldAboveKeyboard(field, keyboardTop, scrollParent, scrollContent, previousPadding);
  };

  // On keyboard geometry changes, run again a frame later AND once floating-ui's async reposition
  // and the keyboard's own slide have landed.
  let settleFrame = 0;
  let settleTimer = 0;
  const handleGeometryChange = () => {
    adjust(true);
    // Re-clamp only, no re-scroll: re-issuing a smooth scroll mid-flight restarts its ease from
    // zero velocity, turning one glide into a stuttering crawl.
    cancelAnimationFrame(settleFrame);
    settleFrame = requestAnimationFrame(() => adjust(false));
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => adjust(false), CONTAINER_SETTLE_MS);
  };
  // Re-check the overlay's clearance as it follows its scrolling anchor — WITHOUT re-scrolling the
  // anchor, which would fight the user's own scrolling (and feed back into this very listener).
  // Coalesced to one pass per frame: scroll events fire per pointermove on touch, and each pass
  // forces a layout read.
  let scrollFrame = 0;
  const handleScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      adjust(false);
    });
  };

  keyboard.addEventListener('virtual-keyboard-toggle', handleGeometryChange);
  keyboard.addEventListener('geometrychange', handleGeometryChange);
  window.addEventListener('resize', handleGeometryChange);
  // Overlay only; capture phase, since scroll events don't bubble. The scroll branch must not react
  // to scrolling at all: its adjust scrolls, so it would loop.
  if (overlay) window.addEventListener('scroll', handleScroll, true);
  handleGeometryChange();

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (activeLiftStop === stop) activeLiftStop = null;
    cancelAnimationFrame(settleFrame);
    cancelAnimationFrame(scrollFrame);
    window.clearTimeout(settleTimer);
    keyboard.removeEventListener('virtual-keyboard-toggle', handleGeometryChange);
    keyboard.removeEventListener('geometrychange', handleGeometryChange);
    window.removeEventListener('resize', handleGeometryChange);
    if (overlay) window.removeEventListener('scroll', handleScroll, true);
    if (overlay) overlay.style.transform = previousTransform;
    if (anchor) {
      anchor.style.scrollMarginTop = previousAnchorMarginTop;
      anchor.style.scrollMarginBottom = previousAnchorMarginBottom;
    }
    field.style.scrollMarginBottom = previousScrollMargin;
    if (scrollContent) scrollContent.style.paddingBottom = previousPadding;
  };
  activeLiftStop = stop;
  return stop;
}
